import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { catalog } from '../../i18n/catalog';

const STORAGE_KEY = 'jassp-locale';

const LanguageContext = createContext(null);

function readStoredLocale() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'fr' || raw === 'en') return raw;
  } catch {
    /* ignore */
  }
  return 'fr';
}

function messageAt(locale, key) {
  const pack = catalog[locale];
  if (pack && Object.prototype.hasOwnProperty.call(pack, key)) {
    return pack[key];
  }
  const fallback = catalog.en[key];
  if (fallback !== undefined) return fallback;
  return catalog.fr[key] ?? key;
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => readStoredLocale());

  const setLocale = useCallback((next) => {
    if (next !== 'fr' && next !== 'en') return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'fr' ? 'fr' : 'en';
  }, [locale]);

  /** Dot-separated keys from `i18n/catalog.js` */
  const t = useCallback(
    (key) => {
      if (!key) return '';
      if (!key.includes('.')) return key;
      return messageAt(locale, key);
    },
    [locale],
  );

  /** Navbar / layout-config short labels (no dots) */
  const tNav = useCallback(
    (label) => {
      if (!label) return '';
      if (locale === 'fr') {
        return catalog.fr._navFromEn[label] ?? label;
      }
      return catalog.en._navFromFr[label] ?? label;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t, tNav }), [locale, setLocale, t, tNav]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

function tNavAt(locale, label) {
  if (!label) return '';
  if (locale === 'fr') {
    return catalog.fr._navFromEn[label] ?? label;
  }
  return catalog.en._navFromFr[label] ?? label;
}

function tAt(locale, key) {
  if (!key) return '';
  if (!key.includes('.')) return key;
  return messageAt(locale, key);
}

/**
 * Returns language helpers from context, or a read-only fallback (localStorage + catalog)
 * if `LanguageProvider` is missing — avoids white screen; prefer wrapping the app in `index.js`.
 */
export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;

  const locale = readStoredLocale();
  return {
    locale,
    setLocale: () => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('[useLanguage] LanguageProvider is missing; language cannot be changed.');
      }
    },
    t: (key) => tAt(locale, key),
    tNav: (label) => tNavAt(locale, label),
  };
}
